import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../client'
import { useAuthStore } from '../../store/authStore'
import { camelKeys } from '../../utils/casing'

// Mirrors apps/mobile/src/api/hooks/useConnections.ts + useNotifications.ts
// against the same backend endpoints. No new endpoints introduced.

// #invalidation-key-fixes — one helper for "a connection changed state".
// The sidebar badges are DELIBERATELY namespaced apart from the page
// queries (['notifications','badge',userId] vs ['notifications',userId])
// so page-level invalidations never prefix-matched them: the header
// count stayed wrong for up to 30s after every accept/reject with no
// way to force it but a reload. Every connection mutation now funnels
// through this helper. ['profile'] is invalidated WITHOUT a userId so
// both parties' cached profiles (keyed by string id) refresh their
// "N connected relatives".
export function invalidateConnectionSurfaces(
  qc: ReturnType<typeof useQueryClient>,
  userId: number | undefined,
): void {
  qc.invalidateQueries({ queryKey: ['notifications', userId] })          // Alerts -> Requests tab
  qc.invalidateQueries({ queryKey: ['notifications', 'badge', userId] }) // sidebar alerts badge
  qc.invalidateQueries({ queryKey: ['mergeProposals', 'badge', userId] })
  qc.invalidateQueries({ queryKey: ['sentRequests', userId] })
  qc.invalidateQueries({ queryKey: ['relatives', userId] })              // Relatives list
  qc.invalidateQueries({ queryKey: ['connected-people', userId] })       // Via-Family intermediary picker
  qc.invalidateQueries({ queryKey: ['family-tree', userId] })            // Tree mode
  qc.invalidateQueries({ queryKey: ['relatives-graph', userId] })        // Graph mode (prefix match)
  qc.invalidateQueries({ queryKey: ['activities', userId] })             // Alerts -> Activity tab
  qc.invalidateQueries({ queryKey: ['profile'] })                        // both parties' profile counts
  qc.invalidateQueries({ queryKey: ['search'] })                         // connection status + counts on cards
}

export interface SearchPrivacyDisclosure {
  message: string
  count: number
}

// #eslint-cleanup-web — `results` typed as unknown[] since callers
// (Search.tsx) cast through SearchResult[] at the consumer boundary;
// the API shape itself is loose enough that locking it here would
// be more friction than it's worth without a backend-side schema export.
export interface SearchResponse {
  results: unknown[]
  privacyDisclosure?: SearchPrivacyDisclosure
}

export function useSearchUsers(params: {
  firstName?: string
  lastName?: string
  motherName?: string
  fatherName?: string
  location?: string
}, enabled: boolean) {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['search', params, userId, enabled],
    queryFn: async () => {
      const res = await apiClient.get<SearchResponse>('/api/users/search', {
        params: { ...params, currentUserId: userId },
      })
      return res.data
    },
    enabled,
    staleTime: 0,
  })
}

export interface RelationType {
  id: number
  name: string
  displayName?: string
  displayNameScript?: string
  isPrimary?: boolean
}

export function useRelationTypes() {
  const user = useAuthStore((s) => s.user)
  const lang = user?.preferredLanguage ?? 'en'
  return useQuery({
    queryKey: ['relationTypes', lang],
    queryFn: async () => {
      const res = await apiClient.get<RelationType[]>('/api/relationships/relation-types', {
        params: { lang },
      })
      return res.data
    },
    staleTime: 1000 * 60 * 10,
  })
}

export function useInverseRelations(relTypeId: number | null) {
  return useQuery({
    queryKey: ['inverseRelations', relTypeId],
    queryFn: async () => {
      const res = await apiClient.get<RelationType[]>(`/api/relationships/inverse/${relTypeId}`)
      return res.data
    },
    enabled: !!relTypeId,
  })
}

export function useSendRequest() {
  const qc = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)
  return useMutation({
    mutationFn: async (data: {
      receiverId: number
      relMode?: 'direct' | 'indirect' | 'social'
      relationTypeId?: number
      relationTypeName?: string
      relationPath?: string[]
      // #suggestions-path-drop — callers that already speak the wire name.
      // The Suggestions manual-connect payload builds `senderRelationPath`
      // directly; without this key in the type it was spread in and then
      // immediately overwritten below by an undefined `data.relationPath`,
      // so the chain the user built was silently dropped in transit.
      senderRelationPath?: string[]
      displayTe?: string
      socialLabel?: string
      throughUserId?: number
      viaPersonName?: string
    }) => {
      const res = await apiClient.post('/api/notifications/send', {
        senderId: userId,
        ...data,
        senderRelationPath: data.relationPath ?? data.senderRelationPath,
        // #send-prepend-always (D2) — declares the /preview contract: the
        // path carries ONLY the picked via-hop atoms, so the server ALWAYS
        // prepends the stored sender→through hop (no first-atoms guessing).
        pathVersion: 2,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['search'] })
      qc.invalidateQueries({ queryKey: ['sentRequests'] })
    },
  })
}

export function usePendingNotifications() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['notifications', userId],
    queryFn: async () => {
      const res = await apiClient.get<Record<string, unknown>[]>(`/api/notifications/pending/${userId}`)
      return res.data.map(camelKeys)
    },
    enabled: !!userId,
    // #auto-refresh Gap 2 — incoming requests are near-real-time. staleTime:0
    // is the load-bearing bit: the global 5-min staleTime otherwise neuters
    // the window-focus / reconnect refetches (RQ defaults them true), so a
    // user returning to the tab saw stale data until the next poll. With
    // staleTime:0 the focus/reconnect refetches actually fire; the 30s poll
    // stays as the steady-state backstop.
    staleTime: 0,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}

export function useAcceptNotification() {
  const qc = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)
  return useMutation({
    mutationFn: async ({
      notificationId,
      recipientRelationType,
    }: {
      notificationId: number
      recipientRelationType?: number
    }) => {
      const res = await apiClient.post(`/api/notifications/${notificationId}/accept`, {
        userId,
        recipientRelationType,
      })
      return res.data
    },
    onSuccess: () => {
      // The accept created a new edge in the user's network; refetch every
      // surface that reads from it so the UI catches up without a manual pull.
      invalidateConnectionSurfaces(qc, userId)
    },
  })
}

export function useRejectNotification() {
  const qc = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)
  return useMutation({
    mutationFn: async (notificationId: number) => {
      const res = await apiClient.post(`/api/notifications/${notificationId}/reject`)
      return res.data
    },
    onSuccess: () => {
      invalidateConnectionSurfaces(qc, userId)
    },
  })
}

export function useSentRequests() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['sentRequests', userId],
    queryFn: async () => {
      const res = await apiClient.get<Record<string, unknown>[]>(`/api/notifications/sent/${userId}`)
      return res.data.map(camelKeys)
    },
    enabled: !!userId,
    // #auto-refresh Gap 2 — mirror the incoming-requests query so status
    // changes (accepted/rejected) surface on focus/reconnect, not just poll.
    staleTime: 0,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}

export function useCancelSentRequest() {
  const qc = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)
  return useMutation({
    mutationFn: async (receiverId: number) => {
      const res = await apiClient.post('/api/notifications/delete', { receiverId })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sentRequests', userId] })
      qc.invalidateQueries({ queryKey: ['search'] })
      qc.invalidateQueries({ queryKey: ['notifications', 'badge', userId] })
    },
  })
}

export function useCounterNotification() {
  const qc = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)
  return useMutation({
    // #counter-perspective / #counter-chain — the counterer states the
    // relation the way they'd send it ("<other person> is my …"), direct or
    // Via Family; the server stores it from the original sender's side.
    mutationFn: async ({
      requestId,
      counterReason,
      ...claim
    }: {
      requestId: number
      counterReason?: string
      relMode: 'direct' | 'indirect'
      relationTypeId?: number
      relationTypeName?: string
      relationPath?: string[]
      throughUserId?: number
      viaPersonName?: string
      displayTe?: string
      ageContext?: 'elder' | 'younger'
    }) => {
      const res = await apiClient.post(`/api/notifications/${requestId}/counter`, {
        ...claim,
        counterReason,
        claimPerspective: 'counterer',
        pathVersion: 2,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', userId] })
      qc.invalidateQueries({ queryKey: ['notifications', 'badge', userId] })
      qc.invalidateQueries({ queryKey: ['sentRequests', userId] })
    },
  })
}
